import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import MarketShell from '../components/market/MarketShell';
import { fetchMarketCards, marketTitle, specText } from '../lib/marketCatalog';
import { marketCategoryLabel, marketLeafLabel } from '../lib/marketCategories';
import { cleanTiers, formatSar, orderStepOf, rangeText, toSar } from '../lib/marketPricing';
import { clearCompare, compareIds, onMarketLocalChange, toggleCompare } from '../lib/marketLocal';
import { mainImage, minOrderQty, unitWord } from '../components/market/MarketProductCard';
import StarRating from '../components/market/StarRating';
import SupplierName from '../components/market/SupplierName';

// Side-by-side comparison (/market/compare) of up to four products chosen on
// cards ("Compare"). Public product data only: price, minimum order and
// step, the supplier's rating (when it has a valid one), wholesale tiers,
// variants, stock, category and the supplier's specifications. A spec a
// product does not state is a dash — never a guess.

const T = {
  ar: { title: 'مقارنة المنتجات', empty: 'اختر «قارن» على منتجين أو أكثر لمقارنتها هنا.', browse: 'تصفح السوق', clear: 'مسح المقارنة', remove: 'إزالة', back: 'السوق',
    price: 'السعر', moq: 'الحد الأدنى للطلب', step: 'يُطلب بمضاعفات', rating: 'تقييم المورد', tiers: 'أسعار الجملة', variants: 'الخيارات', stock: 'المخزون', category: 'القسم', supplier: 'المورد', specs: 'المواصفات',
    tierLine: (q, p) => `${q}+ : ${p}`, none: '—', view: 'عرض المنتج' },
  en: { title: 'Compare products', empty: 'Tick "Compare" on two or more products to compare them here.', browse: 'Browse the market', clear: 'Clear comparison', remove: 'Remove', back: 'Market',
    price: 'Price', moq: 'Minimum order', step: 'Ordered in multiples of', rating: 'Supplier rating', tiers: 'Wholesale prices', variants: 'Variants', stock: 'Stock', category: 'Category', supplier: 'Supplier', specs: 'Specifications',
    tierLine: (q, p) => `${q}+ : ${p}`, none: '—', view: 'View product' },
  zh: { title: '商品对比', empty: '在两个或以上商品上勾选“对比”即可在此对比。', browse: '浏览市场', clear: '清空对比', remove: '移除', back: '市场',
    price: '价格', moq: '起订量', step: '订购倍数', rating: '供应商评分', tiers: '批发价', variants: '规格', stock: '库存', category: '分类', supplier: '供应商', specs: '规格参数',
    tierLine: (q, p) => `${q}+ : ${p}`, none: '—', view: '查看商品' },
};

export default function MarketCompare({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const c = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  usePageTitle('market', lang);
  const [ids, setIds] = useState(() => compareIds());
  const [rows, setRows] = useState(null);
  useEffect(() => onMarketLocalChange(() => setIds(compareIds())), []);
  useEffect(() => { fetchMarketCards(ids).then((r) => setRows(r.rows)); }, [ids]);

  const specNames = [];
  for (const p of rows || []) for (const s of (Array.isArray(p.specs) ? p.specs : [])) if (s?.name_zh && !specNames.some((x) => x.name_zh === s.name_zh)) specNames.push(s);
  const specOf = (p, nz) => (Array.isArray(p.specs) ? p.specs : []).find((s) => s.name_zh === nz);
  const cell = { padding: '8px 10px', borderTop: '1px solid var(--border)', fontSize: 13, verticalAlign: 'top', minWidth: 170 };
  const head = { ...cell, fontWeight: 700, color: 'var(--text-secondary)', minWidth: 130, position: 'sticky', insetInlineStart: 0, background: 'var(--surface)' };
  const line = (label, f, attr) => (
    <tr data-compare-row={attr}>
      <th scope="row" style={head}>{label}</th>
      {rows.map((p) => <td key={p.id} style={cell}>{f(p) ?? c.none}</td>)}
    </tr>
  );

  return (
    <MarketShell lang={lang} user={user}>
      <div className="fx-wrap" style={{ fontFamily: font }}>
        <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/market')}>{isAr ? '→ ' : '← '}{c.back}</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 className={`fx-h1${isAr ? ' ar' : ''}`}>{c.title}</h1>
          {ids.length > 0 && <button type="button" onClick={clearCompare} style={{ background: 'none', border: 'none', color: '#9A7636', textDecoration: 'underline', cursor: 'pointer', fontFamily: font }}>{c.clear}</button>}
        </div>
        {rows === null ? <p>…</p> : rows.length === 0 ? (
          <div data-compare-empty="1"><p className="fx-sub">{c.empty}</p>
            <button type="button" onClick={() => nav('/market')} style={{ padding: '9px 18px', borderRadius: 12, border: 'none', background: '#1A1814', color: '#fff', cursor: 'pointer', fontFamily: font }}>{c.browse}</button></div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface-raised)' }}>
            <table data-compare-table={rows.length} style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...head, borderTop: 'none' }} />
                  {rows.map((p) => (
                    <th key={p.id} style={{ ...cell, borderTop: 'none', textAlign: 'start' }}>
                      <img src={mainImage(p) || ''} alt="" style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 10, display: 'block', marginBottom: 6 }} />
                      <button type="button" onClick={() => nav(`/market/${p.id}`)} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'start', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: font, color: 'var(--text-primary)' }}>{marketTitle(p, lang)}</button>
                      <div><button type="button" data-compare-remove={p.id} onClick={() => toggleCompare(p.id)} style={{ background: 'none', border: 'none', padding: 0, color: '#9A7636', cursor: 'pointer', fontSize: 12, fontFamily: font }}>{c.remove}</button></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {line(c.price, (p) => rangeText(p.price_min_cny, p.price_max_cny, exchangeRates), 'price')}
                {line(c.moq, (p) => `${minOrderQty(p).toLocaleString('en-US')} ${unitWord(p, lang)}`.trim(), 'moq')}
                {line(c.step, (p) => (orderStepOf(p) > 1 ? orderStepOf(p) : null), 'step')}
                {line(c.rating, (p) => (p.seller_rating ? <StarRating rating={p.seller_rating} size={12} compact /> : null), 'rating')}
                {line(c.tiers, (p) => { const t = cleanTiers(p.price_tiers); return t.length > 1 ? t.map((x) => <div key={x.min_quantity} dir="ltr">{c.tierLine(x.min_quantity, formatSar(toSar(x.price_cny, exchangeRates)))}</div>) : null; }, 'tiers')}
                {line(c.variants, (p) => (p.sku_count > 1 ? p.sku_count : null), 'variants')}
                {line(c.stock, (p) => (p.stock_total != null ? Number(p.stock_total).toLocaleString('en-US') : null), 'stock')}
                {line(c.category, (p) => [marketCategoryLabel(p.category, lang), marketLeafLabel(p.subcategory, lang), marketLeafLabel(p.product_type, lang)].filter(Boolean).join(' › ') || null, 'category')}
                {line(c.supplier, (p) => (p.seller_name_latin ? <SupplierName name={p.seller_name_latin} /> : null), 'supplier')}
                {specNames.length > 0 && <tr><th colSpan={rows.length + 1} style={{ ...cell, textAlign: 'start', fontWeight: 700 }}>{c.specs}</th></tr>}
                {specNames.map((s) => line(specText(s, lang).name || s.name_zh, (p) => { const v = specOf(p, s.name_zh); return v ? specText(v, lang).value || v.value_zh : null; }, `spec:${s.name_zh}`))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MarketShell>
  );
}
